import { config } from '@smash-sdk/core';
import { useEffect, useState } from "react";
import { Badge } from "react-bootstrap";
import { useParams } from "react-router-dom";
import {
  RouteDefinition
} from "smash-framework/dist/middlewares/apiGatewayProxyRest/interfaces";
import Divider from "../../components/Divider/Divider";
import DocumentationPage from "../../components/DocumentationPage/DocumentationPage";
import SelectInput from "../../components/SelectInput/SelectInput";
import SmashSpinner from "../../components/SmashSpinner/SmashSpinner";
import SnippetContainer from "../../components/SnippetContainer/SnippetContainer";
import { apiReferenceState, listServiceVersionActions } from "../../features/apiReference/apiReferenceSlice";
import Endpoint from "../../features/apiReference/components/Endpoint";
import SearchBar from "../../components/SearchBar/SearchBar";
import { useAppDispatch, useAppSelector } from "../../hooks/redux/hooks";
import { Helmet } from 'react-helmet-async';

// FIX ME - This is a temporary summary object for services. Should be retrieved from a proper source or API.
const serviceSummaries = {
  "billing": "Billing service is responsible for managing subscription, payments, and invoicing of your account.",
  "customization": "Customization service is responsible for managing team branding, including logo and brackground.",
  "directory": "Directory service is responsible for managing user logins, password management and other user-related functionalities.",
  "discovery": "Discovery service is responsible for managing the discovery of services within the Smash ecosystem.",
  "domain": "Domain service is responsible for managing custom domains.",
  "iam": "IAM service is responsible for managing user identities, groups, teams and permissions.",
  "image": "Image service is responsible for managing image uploads, processing and transformations.",
  "link": "Link service is responsible for managing transfer links.",
  "metric": "Metric service is responsible for collecting and managing metrics.",
  "promotion": "Promotion service is responsible for managing transfers branding during uploas and downloads.",
  "transfer": "Transfer service is responsible for managing file transfers uploads."
};

const APIReferenceService = () => {
  const { service } = useParams();
  const dispatch = useAppDispatch();
  const { references } = useAppSelector(apiReferenceState);
  const [selectedVersion, setSelectedVersion] = useState<string | undefined>(undefined);
  const [filteredRoutes, setFilteredRoutes] = useState<{ [key: string]: RouteDefinition[] } | undefined>(undefined);

  useEffect(() => {
    if (service) {
      setFilteredRoutes(undefined);
      setSelectedVersion(undefined);
      dispatch(listServiceVersionActions({ service }));
    }
  }, [service]);

  useEffect(() => {
    if (references?.latestVersion && !selectedVersion) {
      setSelectedVersion(references?.latestVersion);
    }
  }, [references]);

  const handleSelectedVersion = (version: string) => {
    if (!service) {
      console.error("Service is not defined");
      return;
    }
    setSelectedVersion(version);
    dispatch(listServiceVersionActions({ service, version }));
  };

  const isServiceGlobal = (service: string) => {
    const hosts = config.hosts[service];
    return !!(hosts && (Object.keys(hosts).includes('global') || Object.entries(hosts).every(([region, host]) => host === Object.values(hosts)[0])));
  };

  const getServiceBaseUrl = (service: string, version: string) => {
    return isServiceGlobal(service)
      ? `https://${service}.fromsmash.co?version=${version}`
      : `https://${service}.{region}.fromsmash.co?version=${version}`;
  };

  const getServiceAvailableRegions = (service: string) => {
    const hosts = config.hosts[service];
    if (!hosts) return null;
    return Object.entries(hosts).map(([region]) => (
      <Badge key={region} bg="secondary" className="mx-1">{region}</Badge>
    ));
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const searchTerm = e.target.value.toLowerCase();

    if (!searchTerm) {
      setFilteredRoutes(undefined);
      return;
    }

    if (references?.routes) {
      const filteredRoutes = Object.entries(references.routes).reduce((acc, [key, routes]) => {
        const filteredRoutes = routes.filter((route) => {
          const stringiedRoute = JSON.stringify(route);
          return stringiedRoute.toLowerCase().includes(searchTerm);
        });
        if (filteredRoutes.length > 0) {
          acc[key] = filteredRoutes;
        }
        return acc;
      }, {} as { [key: string]: RouteDefinition[] });
      setFilteredRoutes(filteredRoutes);
    }
  }

  const getEndpoints = () => {
    const endpoints = filteredRoutes || references?.routes;
    if (!endpoints) return null;
    const sortedEntries = Object.entries(endpoints).sort(([a], [b]) =>
      a.toLowerCase().localeCompare(b.toLowerCase())
    );
    return sortedEntries.map(([key, routes], idx) => (
      <div className="mb-3" key={idx}>
        <div className="text-primary fw-bold mb-2" key={idx}>
          {key}
        </div>
        {routes?.map((route: RouteDefinition, routeIdx: number) => (
          <Endpoint route={route} key={routeIdx} />
        ))}
      </div>
    ));
  };

  const getServiceName = (service: string) => {
    return service.charAt(0).toUpperCase() + service.slice(1);
  };

  return (
    <DocumentationPage heading={`API Reference - ${getServiceName(service as string)}`}>
      <Helmet>
        <title>Smah API Reference - {getServiceName(service as string)}</title>
        <meta name="description" content={`Smash API Reference for the ${getServiceName(service as string)} service.`} />
      </Helmet>
      {!references?.isLoading && service && references?.isSuccess && selectedVersion && (
        <>
          <div className="row">
            <div className="col-12">
              <div className="mb-3">
                <div className="row">
                  <div className="col">
                    <div className="mb-2">
                      <p>
                        {serviceSummaries[service as keyof typeof serviceSummaries]}
                      </p>
                      <p>Here you will find all the available endpoints for the {getServiceName(service as string)} service.</p>
                    </div>
                    <div>
                      <SnippetContainer
                        title="Base url"
                        code={getServiceBaseUrl(service as string, selectedVersion as string)}
                        copyEnabled={true}
                      />
                    </div>
                  </div>
                  <div className="col">
                    {!references?.isLoading &&
                      references?.availableVersions &&
                      references.availableVersions.length > 0 &&
                      selectedVersion && (
                        <>
                          <div className="mb-3">
                            <span>API Version</span>
                            <SelectInput
                              isSearchable={false}
                              defaultValue={selectedVersion}
                              onChange={({ value }) => handleSelectedVersion(value)}
                              options={references.availableVersions.map(version => ({ value: version, label: version }))}
                            />
                          </div>
                          <div className="mb-3">
                            {!isServiceGlobal(service as string) && (
                              <div>
                                <b>Available regions</b> : {getServiceAvailableRegions(service as string)}
                              </div>
                            )}
                          </div>
                        </>
                      )}

                  </div>
                </div>
              </div>
            </div>
          </div>


          <Divider />



          <div className="row">
            <h2 className="mt-1 mb-3">
              <b>Endpoints</b>
            </h2>
            {references?.isSuccess && service && references?.routes && (
              <>
                <div className="mb-2">
                  <SearchBar handleSearch={handleSearch} />
                  {filteredRoutes && Object.keys(filteredRoutes).length > 0 && (
                    <>
                      <span className="text-secondary">
                        {Object.keys(filteredRoutes).reduce((acc, key) => {
                          return acc + filteredRoutes[key].length;
                        }, 0)} results
                      </span>
                    </>
                  )}
                </div>
                <>{getEndpoints()}</>
              </>
            )}
          </div>
        </>
      )}

      {references?.isLoading && (
        <div className="d-flex justify-content-center">
          <SmashSpinner />
        </div>
      )}
    </DocumentationPage>
  );
};

export default APIReferenceService;